import React from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, MessageCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatFCFA, waLink } from "../lib/constants";
import GoldDivider from "../components/GoldDivider";

export default function Cart() {
  const { items, updateQuantity, removeItem, totalPrice, totalItems } = useCart();

  const waMessage =
    "Bonjour MaxiChoix ! Je souhaite commander :\n\n" +
    items.map((i) => `• ${i.name} x${i.quantity} — ${formatFCFA(i.price * i.quantity)}`).join("\n") +
    `\n\n*Total : ${formatFCFA(totalPrice)}*`;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={56} className="mx-auto text-neutral-300 mb-6" />
        <h1 className="font-display text-4xl text-[#0A0A0A] mb-3">Votre panier est vide</h1>
        <p className="text-neutral-500 mb-8">Découvrez notre sélection premium pour toute la famille.</p>
        <Link to="/boutique" className="btn-gold px-8 py-4 rounded-sm inline-flex">
          Continuer mes achats <ArrowRight size={18} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="cart-page">
      <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A] mb-2">Mon panier</h1>
      <p className="text-neutral-500 mb-10">{totalItems} article(s)</p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        {/* Items */}
        <div className="space-y-6">
          {items.map((item) => (
            <div key={item.product_id} className="flex gap-4 border-b border-neutral-100 pb-6" data-testid={`cart-item-${item.slug}`}>
              <Link to={`/produit/${item.slug}`} className="w-24 h-24 md:w-28 md:h-28 bg-[#FBF9F4] rounded overflow-hidden shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1">
                <div className="flex justify-between gap-4">
                  <Link to={`/produit/${item.slug}`} className="font-display text-lg md:text-xl text-[#0A0A0A] hover:text-[#B8860B] transition leading-snug">
                    {item.name}
                  </Link>
                  <button onClick={() => removeItem(item.product_id)} className="text-neutral-400 hover:text-red-500 transition shrink-0" data-testid={`remove-${item.slug}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
                <p className="text-[#B8860B] font-medium mt-1">{formatFCFA(item.price)}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-neutral-200 rounded-sm">
                    <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="px-2.5 py-2 text-neutral-600 hover:text-[#B8860B]">
                      <Minus size={14} />
                    </button>
                    <span className="w-8 text-center text-sm">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="px-2.5 py-2 text-neutral-600 hover:text-[#B8860B]">
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="font-display text-lg text-[#0A0A0A]">{formatFCFA(item.price * item.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 h-fit bg-[#0A0A0A] text-white rounded-lg p-8">
          <h3 className="font-display text-2xl mb-6">Récapitulatif</h3>
          <div className="space-y-3 text-sm mb-6">
            <div className="flex justify-between text-neutral-300">
              <span>Sous-total</span>
              <span>{formatFCFA(totalPrice)}</span>
            </div>
            <div className="flex justify-between text-neutral-300">
              <span>Livraison</span>
              <span className="text-[#F4E5A1]">Calculée à la commande</span>
            </div>
          </div>
          <GoldDivider className="mb-6" />
          <div className="flex justify-between items-baseline mb-8">
            <span className="text-lg">Total</span>
            <span className="font-display text-3xl gold-text">{formatFCFA(totalPrice)}</span>
          </div>
          <Link to="/commande" className="btn-gold w-full py-4 rounded-sm mb-3" data-testid="checkout-btn">
            Passer la commande <ArrowRight size={18} />
          </Link>
          <a
            href={waLink(waMessage)}
            target="_blank" rel="noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-4 rounded-sm font-medium hover:bg-[#1fbb59] transition"
            data-testid="cart-whatsapp-order"
          >
            <MessageCircle size={18} /> Commander sur WhatsApp
          </a>
          <Link to="/boutique" className="block text-center text-sm text-neutral-400 hover:text-[#F4E5A1] mt-5 transition">
            Continuer mes achats
          </Link>
        </div>
      </div>
    </div>
  );
}
